import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { psiTokens } from '../../theme/psiTheme';
import { useLanguage } from '../../contexts/LanguageContext';

export default function Hero() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useLanguage();

  return (
    <Box
      id="home"
      sx={{
        backgroundColor: psiTokens.background.tertiary,
        py: isMobile ? 6 : 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Accent bar on the left */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '4px',
          height: '100%',
          background: `linear-gradient(180deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={isMobile ? 3 : 6} alignItems="center">
          {/* Left content */}
          <Grid item xs={12} md={6}>
            {/* Main headline */}
            <Typography
              variant="h1"
              sx={{
                color: psiTokens.text.primary,
                marginBottom: 2,
                fontWeight: 900,
              }}
            >
              {t('hero.title')}
            </Typography>

            {/* Eyebrow text — under the headline, not above it, so the
                smallest text on the page never reads as coming "before"
                (and thus competing with) the title. See "Typography
                hierarchy" in CLAUDE.md. */}
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: psiTokens.primary.light,
                marginBottom: 3,
              }}
            >
              {t('hero.eyebrow')}
            </Typography>

            {/* Tagline/description */}
            <Typography
              sx={{
                fontSize: 'clamp(16px, 2.2vw, 18px)',
                fontWeight: 400,
                color: psiTokens.text.tertiary,
                lineHeight: 1.65,
                marginBottom: 4,
                maxWidth: '580px',
              }}
            >
              {t('hero.tagline')}
            </Typography>

            {/* CTA Buttons */}
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                size="large"
                sx={{
                  background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
                  color: '#ffffff',
                  padding: '12px 28px',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  borderRadius: '10px',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                {t('hero.ctaPrimary')}
              </Button>

              <Button
                component={RouterLink}
                to="/services"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: psiTokens.primary.light,
                  color: psiTokens.primary.light,
                  padding: '12px 28px',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  borderRadius: '10px',
                  width: isMobile ? '100%' : 'auto',
                  '&:hover': {
                    backgroundColor: 'rgba(167, 111, 204, 0.05)',
                    borderColor: psiTokens.primary.light,
                  },
                }}
              >
                {t('hero.ctaSecondary')}
              </Button>
            </Stack>
          </Grid>

          {/* Right image */}
          {!isMobile && (
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingBottom: '100%',
                  borderRadius: '25px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
              >
                <Box
                  component="img"
                  src="/images/fidel/fidel.jpg"
                  alt="Fidel Gutiérrez, PSI founder and lead consultant"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}
